import {
  AfterViewInit,
  Component,
  EventEmitter,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { take } from 'rxjs/operators';
import { Subject, SubjectUserSecurity } from '../../../shared/models/subject';
import { LayoutService } from '../../../shared/services/layout.service';
import { SubjectDialogComponent } from '../../content/subjects/subject-dialog/subject-dialog.component';
import { WidgetService } from '../widget.service';
import { CardWidgetComponent } from './card-widget/card-widget.component';
import { AuthService } from 'app/shared/services/auth.service';
import { Grid } from 'app/shared/helpers/grid';
import { SubjectService } from 'app/shared/services/subject.service';
import { Subscription } from 'rxjs';
import { EventService } from 'app/shared/services/event.service';
import { UserService } from 'app/shared/services/user.service';

@Component({
  selector: 'subject-card-widget',
  template: `<card-widget
    #cardwidget
    class="h-100-p"
    [title]="title"
    [secondaryTitle]="secondaryTitle"
    [titleBackgroundColor]="titleBackgroundColor"
    [markers]="numberOfMarkers"
    [restricted]="_subject?.restricted"
    [allowEdit]="allowSubjectEdit && _allowEdit && userCanEdit"
    [widgetStyle]="style"
    [widgets]="grid?.items"
    [deadlineDaysLeft]="deadlineDaysLeft"
    [useWorkingDays]="useWorkingDays"
    (onEdit)="edit()"
  ></card-widget>`,
})
export class SubjectCardWidgetComponent extends CardWidgetComponent implements AfterViewInit {
  title: string;
  secondaryTitle: string;
  titleBackgroundColor: string;
  numberOfMarkers = 0;
  style: any;
  allowSubjectEdit = false;
  _allowEdit = true;

  _subject: Subject;
  _resizeToFitContentX = false;
  _resizeToFitContentY = false;

  userReadOnly = false;
  grid = new Grid();
  sizeX = 400;
  sizeY = 300;

  accept: Subscription;
  deadlineDaysLeft: number = 999999;
  useWorkingDays: boolean = true;

  onCurrentUserChanged$: Subscription;
  userCanEdit: boolean = false;
  userCanDelete: boolean = false;

  @Input() set subject(val: Subject) {
    if (val) {
      this.update(val);
    }
  }

  @Input() set resizeToFitContentX(val: boolean) {
    this._resizeToFitContentX = val;
    this.updateStyle(this._resizeToFitContentX, this._resizeToFitContentY);
  }

  @Input() set resizeToFitContentY(val: boolean) {
    this._resizeToFitContentY = val;
    this.updateStyle(this._resizeToFitContentX, this._resizeToFitContentY);
  }

  // @ts-ignore
  @Input() set allowEdit(val: boolean) {
    this._allowEdit = val;
  }

  @Output() onLayoutChanged: EventEmitter<any> = new EventEmitter();

  constructor(
    private layoutService: LayoutService,
    private widgetService: WidgetService,
    private authService: AuthService,
    private subjectService: SubjectService,
    private eventService: EventService,
    private userService: UserService,
    public dialog: MatDialog
  ) {
    super();
    this.userReadOnly = this.authService.isUserReadOnly();

    this.accept = this.eventService.onAcceptSubject.subscribe((subject) => {
      if (
        this._subject &&
        this._subject.id === subject.id &&
        subject.isReferral
      ) {
        // subject is a referral which has just been accepted.  Disable edit for this card
        this.allowEdit = false;
      }
    });
  }

  ngAfterViewInit(): void {
    this.subjectService
      .getSubjectUserSecurity(this.subjectService.currentItem, this.userService.getCurrentUserId())
      .subscribe((result: SubjectUserSecurity) => {
        if (!result || !result.restrictAccess) {
          this.userCanEdit = true;
          this.userCanDelete = true;
        } else {
          this.userCanEdit = result.canEdit;
          this.userCanDelete = result.canDelete;
        }
      });
  }

  ngOnDestroy(): void {
    if (this.accept) {
      this.accept.unsubscribe();
    }
  }

  updateStyle(resizeToFitContentX, resizeToFitContentY) {
    const h = resizeToFitContentY;
    const w = resizeToFitContentX;
    if (h) {
      this.style = w
        ? { 'height.px': this.sizeY + 50, 'width.px': this.sizeX }
        : { 'height.px': this.sizeY + 50 };
      return;
    } else {
      this.style = w ? { 'width.px': this.sizeX } : undefined;
    }
  }

  update(subject: Subject, subjectTypeId?: number) {
    if (subject) {
      const categoryName = 'categoryid';

      subject = new Subject(subject);
      this.allowSubjectEdit = subject.uid && !subject.archived && !this.userReadOnly ? true : false;

      if (subject?.subjectType?.name.toLowerCase() == 'case') {
        this.deadlineDaysLeft = subject.getDataFieldValue('deadlineDaysLeft');
        if (!this.deadlineDaysLeft || this.deadlineDaysLeft == null) {
          this.deadlineDaysLeft = 99999;
        }
        this.useWorkingDays = subject.getDataFieldValue('useWorkingDays');
        if (this.useWorkingDays !== true && this.useWorkingDays !== false) {
          this.useWorkingDays = true;
        }
      }

      let statusColor = '';
      if (subject?.subjectData?.statusColor) {
        statusColor = subject.subjectData.statusColor;
      }
      // get the template for the subject card
      this.layoutService
        .getLayout(subject.subjectTypeId || subjectTypeId, subject.categoryId)
        .pipe(take(1))
        .subscribe((response: any) => {
          let layout = response;
          // force the layout to be updated with any changes to the subject type, such as colour
          layout.subjectType = undefined;
          this.layoutService
            .hydrate(layout)
            .pipe(take(1))
            .subscribe((hydratedLayout) => {
              layout = hydratedLayout;
              if (layout) {
                layout.subjectType.dataFields =
                  layout.subjectType.dataFields.filter(
                    (field) => field.name !== 'Unique Reference'
                  );
                this.titleBackgroundColor = statusColor ? statusColor : layout.subjectType.color;              
                this.title = this.title || layout.subjectType.name;
                this.secondaryTitle = subject.getSummary(layout);
                this.numberOfMarkers = subject.subjectMarkers
                  ? subject.subjectMarkers.length
                  : 0;

                const template =
                  subject.isReferral && layout.referralCardTemplate.id
                    ? layout.referralCardTemplate
                    : layout.cardTemplate;

                // get the layoutitems from the card template
                const options = template.getSizeAndOptions(this.gridSize);
                const layoutChanged =
                  this.sizeX !== options.sizeX ||
                  this.sizeY !== options.sizeY + 25;
                this.sizeX = options.sizeX;
                this.sizeY = options.sizeY + 25;

                // setup category info
                const categoryInfo = this.grid.getCategoryInfo(
                  categoryName,
                  subject.categoryId,
                  template,
                  layout.subjectType
                );

                // get widgets + values
                this.grid.items = [];
                this.grid.addItems(
                  subject,
                  this.widgetService,
                  template,
                  layout.subjectType,
                  categoryInfo
                );
                this.grid.values = subject.getMetadata(layout);

                if (layoutChanged) {
                  this.updateStyle(
                    this._resizeToFitContentX,
                    this._resizeToFitContentY
                  );
                  this.onLayoutChanged.emit({
                    layout,
                    sizeX: this.sizeX,
                    sizeY: this.sizeY,
                  });
                }

                this._subject = subject;
              }
            });
        });
    }
  }

  edit() {
    if (this._subject && this._subject.uid) {
      this.subjectService
        .getFullSubject(this._subject.uid)
        .pipe(take(1))
        .subscribe((subject) => {
          this.dialog
            .open(SubjectDialogComponent, {
              data: {
                subject,
                subjectType: this._subject.subjectType,
              },
            })
            .afterClosed()
            .subscribe((result) => {
              if (result) {
                this.update(result.subject, result.subjectTypeId);
              }
            });
        });
    }
  }
}
